import clsx from 'clsx';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ForwardedRef,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { InputProps, InputType, InputValue } from './types';

import { IconType } from '../../types/SharedIconProps';
import { typedForwardRef } from '../../utils/typedForwardRef';
import { typedMemo } from '../../utils/typedMemo';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Tooltip } from '../Tooltip/Tooltip';
import './Input.css';
import { useEndIcon, useStartIcon } from './hooks';
import {
  fromInputEvent,
  handleNumberBeforeInput,
  toDateBoundAttribute,
  toDisplayValue,
} from './utils';

const messages = defineMessages({
  togglePasswordInvisible: {
    id: 'IconButton.hideTooltip',
    defaultMessage: 'Hide',
    description: 'Tooltip for hide icon button',
  },
  togglePasswordVisible: {
    id: 'IconButton.showTooltip',
    defaultMessage: 'Show',
    description: 'Tooltip for show icon button',
  },
});

function InputComponent<T extends InputType>(
  {
    autoCapitalize,
    disabled,
    label,
    max,
    min,
    name,
    placeholder,
    required,
    status,
    type,
    value,
    onChange,
    onKeyDown,
    __private,
  }: InputProps<T>,
  forwardedRef: ForwardedRef<HTMLInputElement>,
) {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isEmpty = value === null || value === '';
  const { ariaDescribedBy, ariaLabelledBy, combobox, id, seamless } =
    __private ?? {};
  const ariaLabel = ariaLabelledBy ? undefined : label;

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  // `type` checks don't narrow free `T`; widen for the implementation body.
  const emitChange = onChange as (next: InputValue) => void;

  const { startIcon, startIconLabel, startIconOnClick } = useStartIcon({
    type,
    inputRef,
  });
  const { endIcon, endIconLabel, endIconOnClick } = useEndIcon({
    inputRef,
    onChange: emitChange,
  });

  const isStartButton = Boolean(startIconOnClick);
  const isEndButton = Boolean(endIconOnClick);

  let passwordIcon;
  if (type === 'password') {
    passwordIcon = isPasswordVisible ? 'visible' : 'invisible';
  }
  const passwordIconLabel = isPasswordVisible
    ? intl.formatMessage(messages.togglePasswordInvisible)
    : intl.formatMessage(messages.togglePasswordVisible);

  let inputType: InputType = type;
  if (type === 'password') {
    inputType = isPasswordVisible ? 'text' : 'password';
  }

  /** Merges `inputRef` with the forwarded ref. */
  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef],
  );

  // Native `beforeinput` — React's `onBeforeInput` listens to keypress/textInput/paste, not this event.
  useEffect(() => {
    if (type !== 'number') {
      return;
    }
    const input = inputRef.current;
    if (!input) {
      return;
    }
    input.addEventListener('beforeinput', handleNumberBeforeInput);
    return () => {
      input.removeEventListener('beforeinput', handleNumberBeforeInput);
    };
  }, [type]);

  return (
    <span
      className={clsx(
        'eds-input--container',
        disabled && 'eds-input--container--disabled',
        seamless && 'eds-input--container--seamless',
      )}
    >
      {startIcon && (
        <Layout
          aria-hidden={startIconLabel ? undefined : true}
          aria-label={startIconLabel}
          as={isStartButton ? 'button' : 'span'}
          className={clsx(
            'eds-input--start-icon',
            isEmpty && 'eds-input--placeholder',
          )}
          disabled={isStartButton ? disabled : undefined}
          type={isStartButton ? 'button' : undefined}
          onClick={startIconOnClick}
        >
          <Icon icon={startIcon as IconType} size="m" />
        </Layout>
      )}
      <input
        aria-activedescendant={combobox?.activeDescendantId}
        aria-autocomplete={combobox ? 'list' : undefined}
        aria-controls={combobox?.controlsId}
        aria-describedby={ariaDescribedBy}
        aria-expanded={combobox ? true : undefined}
        aria-invalid={status === 'critical' || undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-required={required || undefined}
        autoCapitalize={autoCapitalize}
        autoComplete={combobox ? 'off' : undefined}
        className={clsx(
          'eds-input',
          isEmpty && 'eds-input--placeholder',
          seamless && 'eds-input--seamless',
          status && `eds-input--${status}`,
          startIcon && 'eds-input--with-start-icon',
          endIcon && !isEmpty && 'eds-input--with-end-icon',
          passwordIcon && 'eds-input--with-password-icon',
        )}
        disabled={disabled}
        id={id}
        max={toDateBoundAttribute(type, max)}
        min={toDateBoundAttribute(type, min)}
        name={name}
        placeholder={placeholder}
        ref={setInputRef}
        required={required}
        role={combobox ? 'combobox' : undefined}
        type={inputType}
        value={toDisplayValue(value, type)}
        onChange={(e) => emitChange(fromInputEvent(e, type))}
        onKeyDown={onKeyDown}
      />
      {endIcon && !isEmpty && (
        <Layout
          aria-label={endIconLabel}
          as={isEndButton ? 'button' : 'span'}
          className={clsx(
            'eds-input--end-icon',
            passwordIcon && 'eds-input--end-icon--password',
          )}
          disabled={isEndButton ? disabled : undefined}
          type={isEndButton ? 'button' : undefined}
          onClick={endIconOnClick}
        >
          <Icon icon={endIcon as IconType} size="m" />
        </Layout>
      )}
      {passwordIcon && (
        <button
          aria-label={passwordIconLabel}
          className={clsx(
            'eds-input--password-icon',
            isEmpty && 'eds-input--placeholder',
          )}
          disabled={disabled}
          type="button"
          onClick={() => setIsPasswordVisible(!isPasswordVisible)}
        >
          <Tooltip tooltip={passwordIconLabel}>
            <Icon icon={passwordIcon as IconType} size="m" />
          </Tooltip>
        </button>
      )}
    </span>
  );
}

export const Input = typedMemo(typedForwardRef(InputComponent));

Input.displayName = 'Input';
